import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  EditOutlined,
  InfoOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Collapse,
  IconButton,
  Stack,
} from '@mui/material';
import { DataGrid, GRID_STRING_COL_DEF } from '@mui/x-data-grid';

import api from '../../api';
import EditUserRolesActionItem from './EditUserRolesActionItem';
import RoleChip from './RoleChip';
import RolePopover from './RolePopover';
import SearchBar from '../shared/SearchBar'

import { Trans, useTranslation } from 'react-i18next';
import { Context } from '../../context/context';
import { setUser } from '../../context/actions';
import RemoveAdminDialog from './RemoveAdminDialog';

const GridChipCell = (props) => {
  if (props.value == null) {
    return null;
  }

  return (
    <Stack direction="row" spacing={.5}>
      {props.row.roles.map(role => <RoleChip key={role.id} name={role.name} />)}
    </Stack>
  );
};

const chipColumnType = {
  ...GRID_STRING_COL_DEF,
  resizable: false,
  filterable: false,
  sortable: false,
  editable: false,
  groupable: false,
  renderCell: (params) => <GridChipCell {...params} />,
};

function UserAccessPage() {
  const [{ user },] = useContext(Context)
  const [newRoles, setNewRoles] = useState([])
  const [adminDialogOpen, setAdminDialogOpen] = useState(false)
  const [urlParam, setUrlParam] = useSearchParams({ 'search': '' });
  const [allRoles, setAllRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState(urlParam.get('search') || '');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [updateError, setUpdateError] = useState(false)

  const { t } = useTranslation();

  useEffect(() => {
    api.readRoles().then(res => {
      setAllRoles(res)
    })
  }, [])

  useEffect(() => {
    api.readUsers().then(res => {
      setUsers(res)
    })
  }, [])

  useEffect(() => {
    let rows = filterUsers(users, filter)
    setFilteredUsers(rows)
  }, [filter, users])

  const filterUsers = (users, filter) => {
    return users
      .filter(user =>
        user.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase()) ||
        user.roles.filter(
          role =>
            role.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())).length > 0)
  }

  const handleOnFilter = (e) => {
    setFilter(e.target.value)
    setUrlParam({ 'search': e.target.value })
  }

  const handleOnClear = () => {
    setFilter('')
    setUrlParam('')
  }

  const handleOpenPopover = (event) => {
    setPopoverAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setPopoverAnchorEl(null);
  };


  const handleUpdateOwnUser = (roles) => {
    // since this page can only be visited by admins
    // if the roles array does not contain a role "Administator", the user removed is trying to remove it
    if (roles.filter(r => r.name === 'Administrator').length === 0) {
      setAdminDialogOpen(true);
      setNewRoles(roles);
    } else {
      let u = {
        ...user,
        roles
      };

      api.updateUser(user.id, u).then(res => {
        setUser(u);
        api.readUsers().then(res => {
          setUsers(res);
        })
      }, err => {
        handleUpdateError();
      });
    }
  };

  const handelRemoveOwnAdminPrivileges = () => {
    let u = {
      ...user,
      roles: newRoles,
    };

    api.updateUser(user.id, u).then(res => {
      setUser(u);
    }, err => {
      handleUpdateError();
    });
  };

  const handleUpdateOtherUser = (userId, roles) => {
    let u = users.find(u => u.id === userId);
    u.roles = roles;

    api.updateUser(userId, u).then(res => {
      api.readUsers().then(res => {
        setUsers(res);
      });
    }, err => {
      handleUpdateError();
    });
  };

  const handleUpdateUserRoles = (userId, roles) => {
    // check if selected user is the current user, if yes,
    // there might be extra steps to take (admin rights)
    if (userId === user.id) {
      handleUpdateOwnUser(roles);
    } else {
      handleUpdateOtherUser(userId, roles);
    }
  };

  const handleUpdateError = () => {
    setUpdateError(true);
    setTimeout(() => setUpdateError(false), 5000);
  };

  const columns = [
    { field: 'name', headerName: 'Name', flex: 0.5 },
    { field: 'id', headerName: t('kennung', { ns: 'userAccessPage', defaultValue: 'Kennung' }), flex: 0.3 },
    {
      field: 'roles',
      headerName: t('rollen', { ns: 'userAccessPage', defaultValue: 'Rollen' }),
      flex: 1,
      renderHeader: (params) => (
        <Stack direction='row'>
          {<Trans i18nKey='rollen' ns='userAccessPage'>Rollen</Trans>}
          <IconButton onClick={handleOpenPopover} size='small'>
            <InfoOutlined />
          </IconButton>
        </Stack>
      ),
      ...chipColumnType
    },
    {
      field: 'actions',
      headerName: t('bearbeiten', { ns: 'userAccessPage', defaultValue: 'Bearbeiten' }),
      flex: 0,
      type: 'actions',
      getActions: (params) => {
        return [
          <EditUserRolesActionItem
            onSubmit={handleUpdateUserRoles}
            userId={params.row.id}
            roles={params.row.roles}
            allRoles={allRoles}
            label="Rollen bearbeiten"
            icon={<EditOutlined />}
          />
        ]
      },
    },
  ];

  return (
    <>
      <Box sx={{ textAlign: 'left' }}>
        <SearchBar
          value={filter}
          onChange={handleOnFilter}
          onClear={handleOnClear}
        />
        <Collapse in={updateError}
          sx={{ mt: 2 }}>
          <Alert icon={false} severity="error">
            <Trans i18nKey='minAdmins' ns='userSettingsPage'>
              Es muss mindestens einen Administator geben.
            </Trans>
          </Alert>
        </Collapse>
        <DataGrid
          sx={{ marginTop: 5 }}
          columns={columns}
          rows={filteredUsers}
          slots={{
            noRowsOverlay: () => "", // otherwise a 1px no Rows will be displayed
          }}
        />
        <RolePopover
          open={Boolean(popoverAnchorEl)}
          anchorEl={popoverAnchorEl}
          onClose={handleClosePopover}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        />
      </Box>
      <RemoveAdminDialog
        open={adminDialogOpen}
        onClose={() => setAdminDialogOpen(false)}
        onSubmit={() => {
          setAdminDialogOpen(false)
          handelRemoveOwnAdminPrivileges()
        }}
      />
    </>
  );
};

export default UserAccessPage
